import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import { XCircle, CheckCircle, Trash2, ArrowRightLeft } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import LeadsHeader from "../../../features/leads/components/LeadsHeader";
import SearchBar from "../../../features/leads/components/SearchBar";
import LeadsGrid from "../../../features/leads/components/LeadsGrid";
import Import from "../../../features/leads/components/Import";
import LeadForm from "../../../features/leads/components/LeadForm";
import LeadDetail from "../../../features/leads/components/LeadDetail";
import ArchiveGrid from "../../../features/leads/components/ArchiveGrid";
import PageAnimator from "../../../components/ui/PageAnimator";
import { useNotification } from "../../../context/NotificationContext";
import { useLoading } from "../../../context/LoadingContext";

export default function Leads({ onDelete }) {
    const [leads, setLeads] = useState([]);
    const [filteredLeads, setFilteredLeads] = useState([]);
    const [search, setSearch] = useState("");
    const [showImport, setShowImport] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showArchives, setShowArchives] = useState(false);
    const [archivedLeads, setArchivedLeads] = useState([]);
    const [filteredArchivedLeads, setFilteredArchivedLeads] = useState([]);
    const [selectedLeads, setSelectedLeads] = useState([]);
    const { showToast } = useNotification();
    const { isLoading } = useLoading();
    const [importResultModal, setImportResultModal] = useState(null);
    const [confirmMultiArchive, setConfirmMultiArchive] = useState(false);
    const [confirmMultiTransform, setConfirmMultiTransform] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const navigate = useNavigate();

    const filterList = (list, searchTerm) => {
        if (!searchTerm) return list;
        const lowercasedValue = searchTerm.toLowerCase();
        return list.filter((item) =>
            Object.values(item).some(
                (field) => field && field.toString().toLowerCase().includes(lowercasedValue)
            )
        );
    };



    useEffect(() => {
        fetchLeads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        console.log("Selected leads:", selectedLeads);
    }, [selectedLeads]);

    useEffect(() => {
        if (selectedLead && !isEditing) {
            window.dispatchEvent(new CustomEvent('collapse-sidebar'));
        }
    }, [selectedLead, isEditing]);

    const fetchLeads = async () => {
        try {
            const res = await api.get(`/leads`);
            setLeads(res.data);
            setFilteredLeads(filterList(res.data, search));
        } catch (err) {
            console.error("Erreur lors de la récupération des leads :", err);
        }
    };

    // ✅ Récupérer les leads archivés
    const fetchArchivedLeads = async () => {
        try {
            const res = await api.get(`/leads`);
            setArchivedLeads(res.data);
            setFilteredArchivedLeads(filterList(res.data, search));
        } catch (err) {
            console.error("Erreur lors de la récupération des leads archivés :", err);
        }
    };

    // ✅ Archiver un lead
    const handleArchiveLead = async (leadId) => {
        try {
            await api.put(`/leads/${leadId}`, { status: "DISQUALIFIED" });
            fetchLeads();
            fetchArchivedLeads();
            setSelectedLeads([]);
            showToast("Lead archivé avec succès", "success");
        } catch (error) {
            console.error("Erreur lors de l'archivage :", error);
            showToast("Erreur lors de l'archivage", "error");
        }
    };

    // ✅ Restaurer un lead
    const handleRestoreLead = async (leadId) => {
        try {
            await api.put(`/leads/${leadId}`, { status: "NEW" });
            fetchLeads();
            fetchArchivedLeads();
            showToast("Lead restauré avec succès", "success");
        } catch (error) {
            console.error("Erreur lors de la restauration :", error);
            showToast("Erreur lors de la restauration", "error");
        }
    };

    const handleDeleteLead = async (leadId) => {
        try {
            await api.delete(`/leads/${leadId}`);
            fetchArchivedLeads();
            showToast("Lead supprimé définitivement", "success");
        } catch (error) {
            console.error("Erreur lors de la suppression :", error);
            showToast("Erreur lors de la suppression", "error");
        }
    };

    const handleSearch = (value) => {
        setSearch(value);
        setFilteredLeads(filterList(leads, value));
        setFilteredArchivedLeads(filterList(archivedLeads, value));
    };

    const handleEditLead = (lead) => {
        setSelectedLead(lead);
        setIsEditing(true);
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setSelectedLead(null);
        setIsEditing(false);
    };

    const handleSyncFlowS = async () => {
        try {
            const res = await api.post(`/leads/sync-flows`, {}, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`
                }
            });
            showToast(res.data.message || "Synchronisation effectuée avec succès.", "success");
        } catch (error) {
            if (error.response && error.response.status === 400) {
                showToast("Connexion FlowS non établie. Veuillez la configurer dans les paramètres.", "error");
            } else {
                showToast("Erreur lors de la synchronisation FlowS.", "error");
            }
        }
    };

    const handleImportResult = (result) => {
        setShowImport(false);
        setImportResultModal(result);
    };

    const handleMultiTransform = async () => {
        try {
            await Promise.all(
                selectedLeads.map((id) =>
                    api.put(`/leads/${id}`, { status: "QUALIFIED" })
                )
            );
            fetchLeads();
            setSelectedLeads([]);
            setConfirmMultiTransform(false);
            showToast(`${selectedLeads.length} lead(s) transformé(s) en prospect(s)`, "success");
        } catch (err) {
            console.error(err);
            showToast("Erreur lors de la transformation", "error");
        }
    };

    const handleMultiArchive = async () => {
        try {
            await Promise.all(
                selectedLeads.map((id) =>
                    api.put(`/leads/${id}`, { status: "DISQUALIFIED" })
                )
            );
            fetchLeads();
            fetchArchivedLeads();
            setSelectedLeads([]);
            setConfirmMultiArchive(false);
            showToast(`${selectedLeads.length} leads archivés`, "success");
        } catch (err) {
            console.error(err);
            showToast("Erreur lors de l'archivage multiple", "error");
        }
    };

    return (
        <div className="flex w-full flex-col bg-workspace p-4 sm:p-8 min-h-full">
            <PageAnimator>
                <LeadsHeader
                    search={search}
                    onSearch={handleSearch}
                    selectedLeads={selectedLeads}
                    onTransformer={() => setConfirmMultiTransform(true)}
                    onArchiver={() => {
                        setConfirmMultiArchive(true);
                    }}
                    onShowImport={() => setShowImport(true)}
                    onShowForm={() => {
                        setIsEditing(false);
                        setShowForm(true);
                    }}
                    onShowArchives={() => {
                        setShowArchives(true);
                        fetchArchivedLeads();
                    }}
                />
                {/* Data Grid and Detail Pane */}
                <div className="flex min-w-0 flex-row flex-1">
                    <div className="flex-1 min-w-0 workspace-card flex flex-col shadow-hp-sm transition-all duration-300">
                        <LeadsGrid
                            leads={filteredLeads}
                            paginationResetKey={search}
                            selectProspect={setSelectedLead}
                            onEdit={handleEditLead}
                            refreshLeads={fetchLeads}
                            selectedLeads={selectedLeads}
                            setSelectedLeads={setSelectedLeads}
                            onSyncFlowS={handleSyncFlowS}
                            onArchiveLead={handleArchiveLead}
                        />
                    </div>

                    <AnimatePresence>
                        {selectedLead && !isEditing && (
                            <motion.div
                                initial={{ width: 0, opacity: 0, marginLeft: 0 }}
                                animate={{ width: "500px", opacity: 1, marginLeft: "1rem" }}
                                exit={{ width: 0, opacity: 0, marginLeft: 0 }}
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                className="h-full flex flex-col overflow-hidden shrink-0"
                            >
                                <LeadDetail
                                    lead={selectedLead}
                                    onClose={() => setSelectedLead(null)}
                                    refreshLeads={fetchLeads}
                                    onTransformSuccess={() => setShowSuccessModal(true)}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Modale Importation */}
                <AnimatePresence>
                    {showImport && <Import onClose={() => setShowImport(false)} onResult={handleImportResult} />}
                </AnimatePresence>

                {/* ✅ Modale Résultat Import (Globale) */}
                <AnimatePresence>
                    {importResultModal && (
                        <motion.div
                            className="fixed inset-0 flex items-center justify-center bg-[rgb(var(--hp-ink-950-rgb)/0.5)] backdrop-blur-md z-[80] p-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                className="workspace-card-elevated w-full max-w-sm p-8 flex flex-col items-center text-center rounded-2xl"
                                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                                animate={{ scale: 1, opacity: 1, y: 0 }}
                                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            >
                                {importResultModal.type === 'success' ? (
                                    <>
                                        <div className="w-16 h-16 rounded-full bg-success-100 flex items-center justify-center mb-6 ring-8 ring-success-50">
                                            <CheckCircle className="w-8 h-8 text-success-600" />
                                        </div>
                                        <h3 className="text-xl font-bold text-primary mb-2">Importation terminée !</h3>
                                        <p className="text-subtle text-sm mb-6">
                                            <span className="font-semibold text-success-600">{importResultModal.inserted}</span> leads ont été ajoutés avec succès.<br />
                                            {importResultModal.ignored > 0 && (
                                                <span className="text-warning-600 mt-2 block">
                                                    {importResultModal.ignored} doublons ignorés.
                                                </span>
                                            )}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <div className="w-16 h-16 rounded-full bg-danger-100 flex items-center justify-center mb-6 ring-8 ring-danger-50">
                                            <XCircle className="w-8 h-8 text-danger-600" />
                                        </div>
                                        <h3 className="text-xl font-bold text-primary mb-2">Erreur d'importation</h3>
                                        <p className="text-subtle text-sm mb-6 text-danger-500">
                                            {importResultModal.message}
                                        </p>
                                    </>
                                )}
                                <button
                                    className={`btn w-full ${importResultModal.type === 'success' ? 'btn-primary' : 'bg-danger-600 hover:bg-danger-700 text-white'}`}
                                    onClick={() => {
                                        setImportResultModal(null);
                                        fetchLeads();
                                    }}
                                >
                                    Terminer et rafraîchir
                                </button>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Modale Formulaire Lead */}
                <AnimatePresence>
                    {showForm && (
                        <LeadForm
                            onClose={closeForm}
                            refreshLeads={fetchLeads}
                            lead={isEditing ? selectedLead : null}
                        />
                    )}
                </AnimatePresence>



                {/* ✅ Modale Archives */}
                <AnimatePresence>
                    {showArchives && (
                        <motion.div
                            className="fixed inset-0 flex items-center justify-center bg-[rgb(var(--hp-ink-950-rgb)/0.4)] backdrop-blur-sm z-50 p-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                className="workspace-card-elevated p-6 w-full max-w-4xl max-h-[85vh] flex flex-col"
                                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            >
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-xl font-bold text-primary">Leads Archivés</h2>
                                    <button onClick={() => setShowArchives(false)} className="workspace-icon-button">
                                        <XCircle className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="flex-1 overflow-auto rounded-lg border border-workspace">
                                    <ArchiveGrid
                                        open={showArchives}
                                        onClose={() => setShowArchives(false)}
                                        leads={filteredArchivedLeads}
                                        onRestore={handleRestoreLead}
                                        onDelete={handleDeleteLead}
                                    />
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ✅ Modale Confirmation Transformation Multiple */}
                <AnimatePresence>
                    {confirmMultiTransform && (
                        <motion.div
                            className="fixed inset-0 flex items-center justify-center bg-[rgb(var(--hp-ink-950-rgb)/0.4)] backdrop-blur-sm z-50 p-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                className="workspace-card-elevated p-8 w-full max-w-sm flex flex-col items-center text-center rounded-2xl"
                                initial={{ scale: 0.95, opacity: 0, y: 10 }}
                                animate={{ scale: 1, opacity: 1, y: 0 }}
                                exit={{ scale: 0.95, opacity: 0, y: 10 }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                            >
                                <div className="w-16 h-16 rounded-full bg-info-100 text-info-600 flex items-center justify-center mb-6 shadow-sm">
                                    <ArrowRightLeft className="w-8 h-8" />
                                </div>
                                <h2 className="text-xl font-bold text-primary mb-2">Transformer en prospects ?</h2>
                                <p className="text-subtle text-sm mb-8">
                                    Êtes-vous sûr de vouloir transformer les <strong>{selectedLeads.length}</strong> leads sélectionnés ?
                                </p>
                                <div className="flex items-center gap-3 w-full">
                                    <button
                                        className="btn btn-secondary flex-1"
                                        onClick={() => setConfirmMultiTransform(false)}
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        className="btn btn-primary bg-info-600 border-info-600 hover:bg-info-700 hover:border-info-700 flex-1 text-white"
                                        onClick={handleMultiTransform}
                                    >
                                        Transformer
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ✅ Modale Confirmation Archive Multiple */}
                <AnimatePresence>
                    {confirmMultiArchive && (
                        <motion.div
                            className="fixed inset-0 flex items-center justify-center bg-[rgb(var(--hp-ink-950-rgb)/0.4)] backdrop-blur-sm z-50 p-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                className="workspace-card-elevated p-8 w-full max-w-sm flex flex-col items-center text-center rounded-2xl"
                                initial={{ scale: 0.9, opacity: 0, y: 10 }}
                                animate={{ scale: 1, opacity: 1, y: 0 }}
                                exit={{ scale: 0.9, opacity: 0, y: 10 }}
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            >
                                <div className="w-16 h-16 rounded-full bg-danger-100 text-danger-600 flex items-center justify-center mb-6 shadow-sm">
                                    <Trash2 className="w-8 h-8" />
                                </div>
                                <h2 className="text-xl font-bold text-primary mb-2">Archiver ces leads ?</h2>
                                <p className="text-subtle text-sm mb-8">
                                    Êtes-vous sûr de vouloir archiver les <strong>{selectedLeads.length}</strong> leads sélectionnés ?
                                </p>
                                <div className="flex items-center gap-3 w-full">
                                    <button
                                        className="btn btn-secondary flex-1"
                                        onClick={() => setConfirmMultiArchive(false)}
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        className="btn btn-primary bg-danger-600 border-danger-600 hover:bg-danger-700 hover:border-danger-700 flex-1 text-white"
                                        onClick={handleMultiArchive}
                                    >
                                        Archiver
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Success Transform Modal */}
                <AnimatePresence>
                    {showSuccessModal && (
                        <motion.div
                            className="fixed inset-0 flex items-center justify-center bg-[rgb(var(--hp-ink-950-rgb)/0.4)] backdrop-blur-sm z-50 p-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <motion.div
                                className="workspace-card-elevated p-8 w-full max-w-sm flex flex-col items-center text-center rounded-2xl"
                                initial={{ scale: 0.9, opacity: 0, y: 10 }}
                                animate={{ scale: 1, opacity: 1, y: 0 }}
                                exit={{ scale: 0.9, opacity: 0, y: 10 }}
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            >
                                <div className="w-16 h-16 rounded-full bg-accent-100 text-accent flex items-center justify-center mb-6 shadow-sm">
                                    <CheckCircle className="w-8 h-8" />
                                </div>
                                
                                <h3 className="text-xl font-bold text-primary mb-2">Félicitations !</h3>
                                <p className="text-subtle mb-8">
                                    Allez contacter le nouveau prospect.
                                </p>
                                
                                <div className="flex gap-3 w-full">
                                    <button 
                                        className="btn btn-secondary flex-1"
                                        onClick={() => setShowSuccessModal(false)}
                                    >
                                        Fermer
                                    </button>
                                    <button 
                                        className="btn btn-accent flex-1 whitespace-nowrap"
                                        onClick={() => navigate("/prospects/prospects")}
                                    >
                                        Aller voir le prospect
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </PageAnimator>

            {/* Barre de Recherche Flottante (En dehors de PageAnimator pour préserver fixed) */}
            <AnimatePresence>
                {!isLoading && !showImport && !showForm && !selectedLead && !showArchives && (
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.95 }}
                        transition={{ duration: 0.4, type: "spring", stiffness: 300, damping: 30 }}
                        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center justify-center pointer-events-none"
                    >
                        <div className="pointer-events-auto bg-[rgb(var(--hp-surface-rgb)/85%)] backdrop-blur-xl p-3 rounded-[2rem] shadow-hp-xl border border-workspace flex items-center gap-3 ring-1 ring-black/5 hover:shadow-hp-2xl transition-all duration-300">
                            <SearchBar
                                search={search}
                                onSearch={handleSearch}
                                selectedLeads={selectedLeads}
                                onTransformer={() => setConfirmMultiTransform(true)}
                                onArchiver={() => setConfirmMultiArchive(true)}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
